import { Badge, Skeleton } from "../design-v2";

/* Shared presentation for the two Design V2 library surfaces
 * (`/program/all` and `/program/cycles`). Presentation only: every piece here
 * receives resolved values and callbacks and owns no search state, no filter
 * state, no fetching and no business filtering semantics. */

export const LIBRARY_STATUS_FILTERS = Object.freeze([
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
]);

export function PlusIcon() {
  return (
    <svg className="lz-library__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function SparkleIcon() {
  return (
    <svg className="lz-library__menu-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4l1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6L12 4Z" />
      <path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8Z" />
    </svg>
  );
}

export function SlidersIcon() {
  return (
    <svg className="lz-library__menu-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg className="lz-library__search-icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5 20 20" />
    </svg>
  );
}

export function BookmarkIcon({ filled = false }) {
  return (
    <svg
      className="lz-library__bookmark-icon"
      data-filled={filled ? "true" : "false"}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M7 4h10a1 1 0 0 1 1 1v15l-6-4-6 4V5a1 1 0 0 1 1-1Z" />
    </svg>
  );
}

export function ChevronRightIcon() {
  return (
    <svg className="lz-library__chevron" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function LibrarySearchField({ value, onChange, label, placeholder = "Search your library..." }) {
  return (
    <div className="lz-library__search">
      <SearchIcon />
      <input
        type="text"
        className="lz-library__search-input"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function LibraryLoading({ label }) {
  return (
    <div className="lz-library__loading" aria-busy="true" data-testid="library-loading">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton
          shape="block"
          height={92}
          aria-label={index === 0 ? label : undefined}
          key={index}
        />
      ))}
    </div>
  );
}

function formatMetadataLabel(value) {
  const normalized = String(value || "").trim();

  if (!normalized) {
    return "Unknown";
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

function toStatusTone(status) {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "published") {
    return "published";
  }

  return normalized === "draft" ? "draft" : "neutral";
}

/* Editorial status + source metadata for a weekly plan library row. Mirrors the
 * pill grammar already shipping on `/program`. */
export function PlanMetadata({ source, status }) {
  const normalizedSource = String(source || "").trim().toLowerCase();
  const sourceTone = normalizedSource === "ai"
    ? "ai"
    : normalizedSource === "manual"
      ? "manual"
      : "neutral";

  return (
    <span className="lz-library__pills">
      <Badge
        className={`lz-library__pill lz-library__pill--status-${toStatusTone(status)}`}
        data-plan-status={String(status || "").trim().toLowerCase()}
      >
        {formatMetadataLabel(status)}
      </Badge>
      <Badge
        className={`lz-library__pill lz-library__pill--source-${sourceTone}`}
        data-plan-source={normalizedSource}
      >
        {formatMetadataLabel(source)}
      </Badge>
    </span>
  );
}

function toTemporalTone(temporalStatus) {
  const normalized = String(temporalStatus || "").trim().toLowerCase();

  if (normalized === "active" || normalized === "past" || normalized === "upcoming") {
    return normalized;
  }

  return "neutral";
}

/* Editorial status + temporal status for a cycle library row. */
export function CycleMetadata({ editorialStatus, temporalStatus }) {
  return (
    <span className="lz-library__pills">
      <Badge
        className={`lz-library__pill lz-library__pill--status-${toStatusTone(editorialStatus)}`}
        data-cycle-status={String(editorialStatus || "").trim().toLowerCase()}
      >
        {formatMetadataLabel(editorialStatus)}
      </Badge>
      <Badge
        className={`lz-library__pill lz-library__pill--temporal-${toTemporalTone(temporalStatus)}`}
        data-cycle-temporal={String(temporalStatus || "").trim().toLowerCase()}
      >
        {formatMetadataLabel(temporalStatus)}
      </Badge>
    </span>
  );
}
